/**
 * semanticLineageService — SENTINEL-CORE-1 / F-SENTINEL-LINEAGE-001
 *
 * Records full semantic lineage for every governed cognition output:
 * input hash → processing chain → output hash. Enables end-to-end
 * traceability from any output back to its originating inputs.
 *
 * Writes to Firestore via DataService and emits SENTINEL-LINEAGE-RECORDED.
 *
 * KTRUST-L1 resilience: fire-and-forget — lineage failure never blocks caller.
 * All Firestore writes through DataService. tenantId on every document.
 */

import DataService from '../DataService';
import EventBusService from '../EventBusService';

function _generateLineageId() {
  const now = Date.now();
  const rand = Math.random().toString(36).slice(2, 12).toUpperCase();
  return `LIN_${now.toString(36).toUpperCase()}_${rand}`;
}

/**
 * Compute a stable string hash of a value for lineage fingerprinting.
 * SHA-256 is ideal; this deterministic djb2-style hash is a lightweight
 * substitute for environments without SubtleCrypto.
 */
function _hashValue(value) {
  if (value == null) return 'null';
  const str = typeof value === 'string' ? value : JSON.stringify(value);
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
    hash = hash >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/**
 * Extract the processing chain from a governed call response.
 * Maps known response fields to chain step descriptors.
 */
function _buildProcessingChain(params, result) {
  const chain = [
    {
      step: `governed_${params.callType || 'aegis'}_call`,
      version: '1',
      outcome: result?.status === 'failed' ? 'blocked' : 'passed',
    },
  ];

  if (result?.enforcerVerdict) {
    chain.push({
      step: 'discipline_enforcer',
      version: result.enforcerVerdict.enforcerVersion || null,
      outcome: result.enforcerVerdict.returnType === 'stated_position' ? 'passed' : 'blocked',
    });
  }

  if (result?.kcritVerdict) {
    chain.push({
      step: 'kcrit_gateway',
      version: null,
      outcome: result.kcritVerdict.verdict === 'approve' ? 'passed' : 'blocked',
    });
  }

  if (result?.contractVerdict) {
    chain.push({
      step: 'presentation_contract',
      version: null,
      outcome: result.contractVerdict.passed ? 'passed' : 'blocked',
    });
  }

  if (result?.provenance) {
    chain.push({
      step: 'aegis8_provenance',
      version: null,
      outcome: 'passed',
    });
  }

  return chain;
}

/**
 * Record semantic lineage for a governed cognition output.
 *
 * @param {object} opts
 * @param {string} opts.tenantId
 * @param {string} opts.agentId
 * @param {string} opts.callType       - 'aegis' | 'artemis' | 'council'
 * @param {string} opts.module
 * @param {string} opts.decisionType
 * @param {object} opts.inputParams    - the original call params (hashed, not stored raw)
 * @param {object} opts.result         - the governed call result (hashed, not stored raw)
 * @returns {Promise<string>} lineageId
 */
export async function recordLineage(opts) {
  const { tenantId, agentId, callType, module: mod, decisionType, inputParams, result } = opts;

  const lineageId = _generateLineageId();
  const timestamp = new Date().toISOString();

  const inputHash = _hashValue({ agentId, module: mod, decisionType, context: inputParams?.context });
  const outputHash = _hashValue(result?.text || result?.outputSummary);
  const processingChain = _buildProcessingChain({ callType }, result);
  const finalDisposition = result?.finalDisposition || 'unknown';
  const decisionId = result?.decisionId || null;

  const record = {
    lineageId,
    tenantId,
    decisionId,
    agentId,
    callType,
    module: mod,
    decisionType,
    inputHash,
    processingChain,
    outputHash,
    finalDisposition,
    timestamp,
    softDeleted: null,
  };

  await DataService.set(
    `sentinel_lineage/${tenantId}/records`,
    lineageId,
    record
  );

  await EventBusService.publish({
    eventType: 'sentinel.lineage.recorded',
    tenantId,
    sourceModule: 'sentinel',
    sourceId: lineageId,
    payload: {
      lineageId,
      decisionId,
      agentId,
      callType,
      module: mod,
      decisionType,
      inputHash,
      processingChain,
      outputHash,
      finalDisposition,
      timestamp,
    },
    actor: { id: agentId, role: 'sentinel_substrate' },
  });

  return lineageId;
}

export default { recordLineage };
